import { executeNavigationAction, getNavigationAction } from '@/lib/navigation';
import { resumePendingPageInteraction } from '@/lib/page-interaction';
import { useEffect, useRef } from 'react';

import type { IAction } from '@chainlit/react-client';

const HANDLED_ACTIONS_KEY = 'ipma-copilot-handled-navigation-actions';
const AUTO_NAVIGATION_DELAY_MS = 450;
const MAX_STORED_ACTIONS = 50;

const readHandledActionIds = (): string[] => {
  try {
    const value = sessionStorage.getItem(HANDLED_ACTIONS_KEY);
    const parsed = value ? JSON.parse(value) : [];
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === 'string')
      : [];
  } catch {
    return [];
  }
};

const persistHandledActionIds = (ids: Set<string>) => {
  try {
    sessionStorage.setItem(
      HANDLED_ACTIONS_KEY,
      JSON.stringify(Array.from(ids).slice(-MAX_STORED_ACTIONS))
    );
  } catch {
    // Navigation still works when storage is unavailable.
  }
};

interface Props {
  actions: IAction[];
}

export const NavigationActionHandler = ({ actions }: Props) => {
  const handledIds = useRef(new Set(readHandledActionIds()));
  const scheduledIds = useRef(new Set<string>());

  useEffect(() => {
    const controller = new AbortController();
    void resumePendingPageInteraction({ signal: controller.signal });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const navigationAction = actions
      .map(getNavigationAction)
      .findLast(
        (action) =>
          action?.behavior === 'auto' &&
          !handledIds.current.has(action.id) &&
          !scheduledIds.current.has(action.id)
      );

    if (!navigationAction) return;

    scheduledIds.current.add(navigationAction.id);

    const timeout = window.setTimeout(() => {
      scheduledIds.current.delete(navigationAction.id);
      handledIds.current.add(navigationAction.id);
      persistHandledActionIds(handledIds.current);
      void executeNavigationAction(navigationAction);
    }, AUTO_NAVIGATION_DELAY_MS);

    return () => {
      window.clearTimeout(timeout);
      scheduledIds.current.delete(navigationAction.id);
    };
  }, [actions]);

  return null;
};
